import { PubSub } from '../PubSub';
import type { GameEvents } from '../events';

const CHAR_DELAY_MS = 5;

function escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;');
}

/**
 * Renders the in-game console overlay. Subscribes to all `console:*` events from
 * the event bus and publishes `console:input-submit` when the player submits text.
 *
 * A full-screen transparent overlay (z-index 15, above the UI layer at z-index 10
 * but below the console at z-index 20) blocks all pointer events while the console
 * is visible, making it behave as a modal.
 */
export class ConsoleView {
    private consoleEl: HTMLElement;
    private codeEl: HTMLElement;
    private inputEl: HTMLInputElement;
    private cursorEl: HTMLElement;
    private overlayEl: HTMLElement;

    private typingCancelled = false;
    private resolveTyping: (() => void) | null = null;
    private typewriterTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(pubsub: PubSub<GameEvents>) {
        this.consoleEl = document.getElementById('console-output')!;
        this.codeEl    = this.consoleEl.querySelector('.code')!;
        this.inputEl   = document.getElementById('console-input') as HTMLInputElement;
        this.cursorEl  = this.codeEl.querySelector('.cursor')!;

        this.overlayEl = document.createElement('div');
        this.overlayEl.style.cssText = 'position:fixed;inset:0;z-index:15;display:none;';
        document.body.appendChild(this.overlayEl);

        // On mobile, programmatic focus() doesn't show the virtual keyboard — only a
        // focus() call that runs directly inside a user-gesture handler does. Tapping
        // anywhere on the blocking overlay re-focuses the input as a genuine gesture.
        this.overlayEl.addEventListener('touchend', () => {
            if (this.inputEl.style.display !== 'none') {
                this.inputEl.focus();
            }
        });

        this.inputEl.addEventListener('blur', () => {
            if (this.inputEl.style.display !== 'none') {
                requestAnimationFrame(() => this.inputEl.focus());
            }
        });

        this.inputEl.addEventListener('input', () => {
            this.inputEl.style.width = this.inputEl.value.length + 'ch';
        });

        this.inputEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                const value = this.inputEl.value;
                this.inputEl.value = '';
                this.inputEl.style.width = '0ch';
                pubsub.publish('console:input-submit', value);
                return;
            }

            // Printable keys are also published individually so single-key prompts
            // (FORCE_ACKNOWLEDGE) can respond to the first keystroke. The default is
            // not prevented, so the character still reaches the input and free-text
            // entry keeps working.
            if (e.key.length === 1) {
                pubsub.publish('console:key', e.key);
            }
        });

        pubsub.subscribe('console:show', () => this.show());
        pubsub.subscribe('console:hide', () => this.hide());
        pubsub.subscribe('console:clear', () => this.clear());
        pubsub.subscribe('console:type-line', (text) => this.typeLine(text));
        pubsub.subscribe('console:type-inline', (text) => this.typeInline(text));
        pubsub.subscribe('console:show-input', () => this.showInput());
        pubsub.subscribe('console:hide-input', () => this.hideInput());
        pubsub.subscribe('console:clear-input', () => this.clearInput());
        pubsub.subscribe('console:stop-typing', () => this.stopTyping());
    }

    /** Shows the console and the blocking overlay. */
    private show(): void {
        this.overlayEl.style.display = 'block';
        this.consoleEl.style.display = 'flex';
    }

    /** Hides the console and overlay, then clears all content. */
    private hide(): void {
        this.overlayEl.style.display = 'none';
        this.consoleEl.style.display = 'none';
        this.clear();
    }

    /**
     * Removes all line divs from the code element while preserving the persistent
     * cursor and input elements.
     */
    private clear(): void {
        // Rescue persistent elements before wiping line divs
        this.codeEl.appendChild(this.cursorEl);
        this.codeEl.appendChild(this.inputEl);
        Array.from(this.codeEl.children).forEach(child => {
            if (child !== this.cursorEl && child !== this.inputEl) {
                this.codeEl.removeChild(child);
            }
        });
    }

    /** Types `text` on a new line. Resolves when typing completes or is cancelled. */
    private typeLine(text: string): Promise<void> {
        const lineEl = document.createElement('div');
        const textEl = document.createElement('span');
        lineEl.appendChild(textEl);
        lineEl.appendChild(this.cursorEl);
        this.codeEl.appendChild(lineEl);
        return this.typeInto(textEl, text);
    }

    /** Appends `text` to the last line. Resolves when typing completes or is cancelled. */
    private typeInline(text: string): Promise<void> {
        const lineEl = this.cursorEl.parentElement;
        if (!lineEl || lineEl === this.codeEl) return this.typeLine(text);
        const textEl = lineEl.querySelector('span:not(.cursor)') as HTMLElement | null;
        if (!textEl) return this.typeLine(text);
        return this.typeInto(textEl, text, /* append */ true);
    }

    /** Cancels any ongoing typewriter animation immediately, resolving its Promise. */
    private stopTyping(): void {
        this.typingCancelled = true;
        if (this.typewriterTimeout !== null) {
            clearTimeout(this.typewriterTimeout);
            this.typewriterTimeout = null;
        }
        this.resolveTyping?.();
        this.resolveTyping = null;
    }

    /**
     * Inserts the input element inline on the current line (before the cursor)
     * and focuses it.
     */
    private showInput(): void {
        const lineEl = this.cursorEl.parentElement;
        if (lineEl && lineEl !== this.codeEl) {
            lineEl.insertBefore(this.inputEl, this.cursorEl);
        }
        this.inputEl.style.display = 'inline-block';
        this.inputEl.focus();
    }

    /** Hides the input element and clears its value. */
    private hideInput(): void {
        this.inputEl.style.display = 'none';
        this.inputEl.value = '';
    }

    /** Empties the input but leaves it visible, so a prompt can be re-offered. */
    private clearInput(): void {
        this.inputEl.value = '';
        this.inputEl.style.width = '0ch';
    }

    /**
     * Animates text into `el` one character at a time at `CHAR_DELAY_MS` per character.
     * Resolves when all characters have been typed or typing is cancelled.
     *
     * @param el - The span element to write into.
     * @param text - The string to type.
     * @param append - When true, appends to the element's existing HTML rather than replacing it.
     */
    private typeInto(el: HTMLElement, text: string, append = false): Promise<void> {
        this.typingCancelled = false;

        return new Promise<void>((resolve) => {
            this.resolveTyping = resolve;

            const existingHtml = append ? el.innerHTML : '';
            let i = 0;

            const tick = () => {
                if (this.typingCancelled) {
                    resolve();
                    return;
                }
                el.innerHTML = existingHtml + escapeHtml(text.slice(0, ++i));
                if (text.length === 0) {
                    el.innerHTML = existingHtml + '&nbsp;'; // ensure empty lines still take up space
                }
                if (i < text.length) {
                    this.typewriterTimeout = setTimeout(tick, CHAR_DELAY_MS);
                } else {
                    this.typewriterTimeout = null;
                    this.resolveTyping = null;
                    resolve();
                }
            };

            tick();
        });
    }
}
